import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

import type Keycloak from 'keycloak-js';
import type { KeycloakInitOptions } from 'keycloak-js';

/**
 * Ersätter @react-keycloak/web (senast uppdaterad 2022) med samma kontrakt:
 * useKeycloak() ger { keycloak, initialized }, providern initierar klienten och
 * renderar om när inloggningsläget ändras.
 *
 * Hakarna sätts i useLayoutEffect med avsikt. Biblioteket satte dem i
 * componentDidMount, som körs i layout-fasen - före barnens passiva effekter.
 * AuthProvider skriver sedan över onAuthSuccess, onAuthRefreshSuccess och
 * onTokenExpired i en useEffect, och den ordningen måste bestå: annars tar
 * providern tyst över token-refresh och hash-fixen efter inloggning.
 */

/**
 * Varför init aldrig blev klar. Visas för användaren, så varje värde har en
 * egen text och åtgärd i ServiceUnavailable.
 *
 * - unreachable: init-promisen avvisades, typiskt för att Keycloak inte gick
 *   att nå (3p-cookie-kontrollen ger upp efter keycloak-js egna 10 s).
 * - timeout: init svarade varken ja eller nej inom tidsgränsen, typiskt en
 *   statusiframe som aldrig svarar (keycloak-js har ingen timeout där).
 * - auth: Keycloak svarade men avvisade sessionen (onAuthError).
 */
export type KeycloakInitError = 'unreachable' | 'timeout' | 'auth';

/**
 * Längre än keycloak-js messageReceiveTimeout (10 s), så att ett avvisat
 * init hinner rapporteras som 'unreachable' innan vår timer säger 'timeout'.
 */
const KEYCLOAK_INIT_TIMEOUT_MS = 12_000;

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
  initError?: KeycloakInitError;
}

const KeycloakContext = createContext<KeycloakContextValue | null>(null);

interface KeycloakProviderProps {
  keycloak: Keycloak;
  initOptions?: KeycloakInitOptions;
  initTimeoutMs?: number;
  children?: React.ReactNode;
}

/**
 * Biblioteket krävde både idToken och token. Efter en omladdning återställs
 * bara token och refreshToken från sessionStorage, så idToken saknas alltid
 * och värdet var false både före och efter en utloggning: setState bailade
 * ur och onAuthLogout gav ingen omrendering. keycloak-js sätter authenticated
 * till false så fort token släpps, så den speglar läget i alla fall.
 */
const isUserAuthenticated = (keycloak: Keycloak) => !!keycloak.authenticated;

export const KeycloakProvider = ({
  keycloak,
  initOptions,
  initTimeoutMs = KEYCLOAK_INIT_TIMEOUT_MS,
  children,
}: KeycloakProviderProps) => {
  const [initialized, setInitialized] = useState(false);
  const [initError, setInitError] = useState<KeycloakInitError>();
  // Exponeras inte, men en ändring ska ge omrendering - som i biblioteket
  const [, setAuthenticated] = useState(false);
  const initStarted = useRef(false);
  const initializedRef = useRef(false);

  useLayoutEffect(() => {
    // keycloak-js kastar om init körs två gånger på samma instans
    if (initStarted.current) return undefined;
    initStarted.current = true;

    // Den första orsaken vinner: onAuthError hinner före init-promisens
    // avvisning, och ett avvisat init hinner före timern.
    const failInit = (reason: KeycloakInitError) => {
      if (initializedRef.current) return;
      setInitError((current) => current ?? reason);
    };

    const updateState = () => {
      initializedRef.current = true;
      setInitialized(true);
      setInitError(undefined);
      setAuthenticated(isUserAuthenticated(keycloak));
    };

    keycloak.onReady = updateState;
    keycloak.onAuthSuccess = updateState;
    keycloak.onAuthRefreshSuccess = updateState;
    keycloak.onAuthLogout = updateState;
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(5);
    };

    keycloak.onAuthError = () => failInit('auth');
    keycloak.onAuthRefreshError = () => {};

    const timeoutId = window.setTimeout(() => failInit('timeout'), initTimeoutMs);

    keycloak
      .init({ onLoad: 'check-sso', ...initOptions })
      .catch(() => failInit('unreachable'))
      .finally(() => window.clearTimeout(timeoutId));

    return () => window.clearTimeout(timeoutId);
  }, [keycloak, initOptions, initTimeoutMs]);

  return (
    <KeycloakContext.Provider value={{ keycloak, initialized, initError }}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);

  if (!context) {
    throw new Error('useKeycloak måste användas inuti KeycloakProvider');
  }

  return context;
};
